/* eslint-disable i18next/no-literal-string -- internal cron tokens, not user-visible strings */
/* eslint-disable no-utc-date-slice/no-utc-date-slice -- WHY: this file IS a date-utils boundary helper for client-side cron iteration; copying a Date instance is the legitimate operation, no timezone reasoning involved */
import {AppDate} from '@/lib/date-utils'

/*
 * WHY: cron-parser was not in the bundle and an external dep added at Phase F
 * is unnecessary — Spring's `CronExpression` is the server-side authority and
 * the client only needs a "next-run" preview. A small 6-field cron iterator
 * here matches Spring's six-field shape (second minute hour day-of-month
 * month day-of-week) and falls back to "invalid" on anything we can't model.
 */

const DOW_TOKENS: Record<string, number> = {
    SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
}

const MONTH_TOKENS: Record<string, number> = {
    JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
    JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
}

type FieldRange = {min: number; max: number; tokens?: Record<string, number>}

const FIELDS: FieldRange[] = [
    {min: 0, max: 59},                       // second
    {min: 0, max: 59},                       // minute
    {min: 0, max: 23},                       // hour
    {min: 1, max: 31},                       // day of month
    {min: 1, max: 12, tokens: MONTH_TOKENS}, // month
    {min: 0, max: 6, tokens: DOW_TOKENS},    // day of week
]

function parseToken(token: string, tokens?: Record<string, number>): number | null {
    const upper = token.toUpperCase()
    if (tokens && upper in tokens) return tokens[upper]
    if (/^\d+$/.test(upper)) return parseInt(upper, 10)
    return null
}

function expandPart(part: string, field: FieldRange): Set<number> | null {
    if (part === '*' || part === '?') {
        const out = new Set<number>()
        for (let i = field.min; i <= field.max; i++) out.add(i)
        return out
    }
    if (part.includes(',')) {
        const merged = new Set<number>()
        for (const sub of part.split(',')) {
            const s = expandPart(sub, field)
            if (!s) return null
            s.forEach(v => merged.add(v))
        }
        return merged
    }
    let step = 1
    let range = part
    if (part.includes('/')) {
        const [r, st] = part.split('/')
        const stepN = parseInt(st, 10)
        if (Number.isNaN(stepN) || stepN <= 0) return null
        step = stepN
        range = r
    }
    let start: number
    let end: number
    if (range === '*' || range === '?') {
        start = field.min
        end = field.max
    } else if (range.includes('-')) {
        const [lo, hi] = range.split('-')
        const loN = parseToken(lo, field.tokens)
        const hiN = parseToken(hi, field.tokens)
        if (loN === null || hiN === null) return null
        start = loN
        end = hiN
    } else {
        const single = parseToken(range, field.tokens)
        if (single === null) return null
        if (single < field.min || single > field.max) return null
        if (step === 1) {
            return new Set([single])
        }
        start = single
        end = field.max
    }
    if (start < field.min || end > field.max || start > end) return null
    const out = new Set<number>()
    for (let v = start; v <= end; v += step) out.add(v)
    return out
}

export interface ParsedCron {
    seconds: Set<number>
    minutes: Set<number>
    hours: Set<number>
    daysOfMonth: Set<number>
    months: Set<number>
    daysOfWeek: Set<number>
    domAny: boolean
    dowAny: boolean
}

export function parseCron(expr: string): ParsedCron | null {
    if (!expr) return null
    const parts = expr.trim().split(/\s+/)
    if (parts.length !== 6) return null
    const fields: Set<number>[] = []
    for (let i = 0; i < 6; i++) {
        const s = expandPart(parts[i], FIELDS[i])
        if (!s || s.size === 0) return null
        fields.push(s)
    }
    return {
        seconds: fields[0],
        minutes: fields[1],
        hours: fields[2],
        daysOfMonth: fields[3],
        months: fields[4],
        daysOfWeek: fields[5],
        domAny: parts[3] === '*' || parts[3] === '?',
        dowAny: parts[5] === '*' || parts[5] === '?',
    }
}

export function isValidCron(expr: string): boolean {
    return parseCron(expr) !== null
}

const MAX_ITERATIONS = 366 * 24 * 60 * 60

/*
 * WHY: Spring CronExpression iterates per-second. For client preview we walk
 * second-by-second from `from` and stop at the first match — capped at 366d
 * to avoid runaway loops on edge cron strings that never fire (e.g. Feb 31).
 */
export function nextRun(expr: string, from: Date = AppDate.now()): Date | null {
    const parsed = parseCron(expr)
    if (!parsed) return null
    const d = new Date(from.getTime())
    d.setMilliseconds(0)
    d.setSeconds(d.getSeconds() + 1)
    for (let i = 0; i < MAX_ITERATIONS; i++) {
        const sec = d.getSeconds()
        const min = d.getMinutes()
        const hr = d.getHours()
        const dom = d.getDate()
        const month = d.getMonth() + 1
        const dow = d.getDay()
        if (!parsed.seconds.has(sec)) {
            d.setSeconds(d.getSeconds() + 1)
            continue
        }
        if (!parsed.minutes.has(min)) {
            d.setSeconds(0)
            d.setMinutes(d.getMinutes() + 1)
            continue
        }
        if (!parsed.hours.has(hr)) {
            d.setSeconds(0)
            d.setMinutes(0)
            d.setHours(d.getHours() + 1)
            continue
        }
        if (!parsed.months.has(month)) {
            d.setSeconds(0)
            d.setMinutes(0)
            d.setHours(0)
            d.setDate(1)
            d.setMonth(d.getMonth() + 1)
            continue
        }
        const domMatch = parsed.daysOfMonth.has(dom)
        const dowMatch = parsed.daysOfWeek.has(dow)
        let dayOk: boolean
        if (parsed.domAny && !parsed.dowAny) dayOk = dowMatch
        else if (!parsed.domAny && parsed.dowAny) dayOk = domMatch
        else dayOk = domMatch && dowMatch
        if (!dayOk) {
            d.setSeconds(0)
            d.setMinutes(0)
            d.setHours(0)
            d.setDate(d.getDate() + 1)
            continue
        }
        return d
    }
    return null
}

export const PRESET_DAILY = '0 0 9 * * *'
export const PRESET_WEEKLY = '0 0 9 * * MON'
export const PRESET_MONTHLY = '0 0 9 1 * *'

const DOW_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const
export type DayOfWeekToken = typeof DOW_NAMES[number]

export function buildWeeklyCron(days: DayOfWeekToken[]): string {
    if (days.length === 0) return PRESET_WEEKLY
    const sorted = [...days].sort((a, b) => DOW_NAMES.indexOf(a) - DOW_NAMES.indexOf(b))
    return `0 0 9 * * ${sorted.join(',')}`
}

export function buildMonthlyCron(day: number): string {
    const safe = Math.min(31, Math.max(1, Math.floor(day)))
    return `0 0 9 ${safe} * *`
}

export const CRON_PRESETS = {
    NONE: 'NONE',
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
    CUSTOM: 'CUSTOM',
} as const
export type CronPreset = typeof CRON_PRESETS[keyof typeof CRON_PRESETS]

export function detectPreset(expr: string | null): CronPreset {
    if (!expr) return 'NONE'
    const trimmed = expr.trim()
    if (trimmed === PRESET_DAILY) return 'DAILY'
    if (/^0 0 9 \* \* (MON|TUE|WED|THU|FRI|SAT|SUN)(,(MON|TUE|WED|THU|FRI|SAT|SUN))*$/.test(trimmed)) {
        return 'WEEKLY'
    }
    if (/^0 0 9 \d{1,2} \* \*$/.test(trimmed)) {
        return 'MONTHLY'
    }
    return 'CUSTOM'
}

export function extractWeeklyDays(expr: string): DayOfWeekToken[] {
    const m = /^0 0 9 \* \* ([A-Z,]+)$/.exec(expr.trim())
    if (!m) return ['MON']
    return m[1].split(',').filter((t): t is DayOfWeekToken => DOW_NAMES.includes(t as DayOfWeekToken))
}

export function extractMonthlyDay(expr: string): number {
    const m = /^0 0 9 (\d{1,2}) \* \*$/.exec(expr.trim())
    if (!m) return 1
    return parseInt(m[1], 10)
}
